import { useState, useEffect, useMemo, useCallback } from 'react';
import { Mail, Phone, Building2, Calendar, Check, Clock, Trash2, Eye } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Pagination from '../../components/ui/Pagination';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ITEMS_PER_PAGE = 15;

export default function AdminMessages() {
  const { token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Paginated messages
  const paginatedMessages = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return messages.slice(start, start + ITEMS_PER_PAGE);
  }, [messages, currentPage]);

  const totalPages = Math.ceil(messages.length / ITEMS_PER_PAGE);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const markAsRead = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/messages/${id}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setMessages(messages.map(m => m.id === id ? { ...m, status: 'read' } : m));
        if (selectedMessage?.id === id) {
          setSelectedMessage({ ...selectedMessage, status: 'read' });
        }
      }
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/messages/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setMessages(messages.filter(m => m.id !== id));
        if (selectedMessage?.id === id) {
          setSelectedMessage(null);
        }
      }
    } catch (err) {
      console.error('Error deleting message:', err);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const unreadCount = messages.filter(m => m.status === 'new').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 h-[calc(100vh-180px)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div>
          <h1 className="text-2xl font-bold">Messages de contact</h1>
          <p className="text-muted-foreground">
            {unreadCount > 0 ? (
              <span className="text-primary font-medium">{unreadCount} nouveau(x) message(s)</span>
            ) : (
              'Tous les messages sont lus'
            )}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1 min-h-0">
        {/* Messages List */}
        <div className="bg-card rounded-xl border border-border overflow-hidden flex flex-col">
          <div className="p-4 border-b border-border bg-muted/30 flex-shrink-0">
            <h2 className="font-semibold">Boîte de réception ({messages.length})</h2>
          </div>
          <div className="divide-y divide-border flex-1 overflow-y-auto">
            {paginatedMessages.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Mail className="h-12 w-12 mx-auto mb-4 opacity-20" />
                <p>Aucun message</p>
              </div>
            ) : (
              paginatedMessages.map((msg) => (
                <div
                  key={msg.id}
                  onClick={() => {
                    setSelectedMessage(msg);
                    if (msg.status === 'new') markAsRead(msg.id);
                  }}
                  className={`p-4 cursor-pointer transition-colors hover:bg-muted/50 ${
                    selectedMessage?.id === msg.id ? 'bg-primary/5 border-l-4 border-primary' : ''
                  } ${msg.status === 'new' ? 'bg-primary/5' : ''}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {msg.status === 'new' && (
                          <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                        )}
                        <span className={`font-medium truncate ${msg.status === 'new' ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {msg.name}
                        </span>
                      </div>
                      {msg.company && (
                        <p className="text-sm text-muted-foreground truncate">{msg.company}</p>
                      )}
                      <p className="text-sm text-muted-foreground truncate mt-1">
                        {msg.message.substring(0, 80)}...
                      </p>
                    </div>
                    <div className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatDate(msg.createdAt)}
                    </div>
                  </div>
                  {msg.projectType && (
                    <span className="inline-block mt-2 px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                      {msg.projectType}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={messages.length}
            itemsPerPage={ITEMS_PER_PAGE}
          />
        </div>

        {/* Message Detail */}
        <div className="bg-card rounded-xl border border-border overflow-hidden flex flex-col">
          {selectedMessage ? (
            <>
              <div className="p-4 border-b border-border bg-muted/30 flex items-center justify-between flex-shrink-0">
                <h2 className="font-semibold">Détail du message</h2>
                <div className="flex items-center gap-2">
                  {selectedMessage.status === 'new' && (
                    <button
                      onClick={() => markAsRead(selectedMessage.id)}
                      className="p-2 hover:bg-muted rounded-lg transition-colors"
                      title="Marquer comme lu"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => deleteMessage(selectedMessage.id)}
                    className="p-2 hover:bg-red-500/10 text-red-500 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-6 flex-1 overflow-y-auto">
                {/* Contact Info */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-lg font-bold text-primary">
                        {selectedMessage.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{selectedMessage.name}</h3>
                      {selectedMessage.company && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Building2 className="h-3 w-3" />
                          {selectedMessage.company}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact Details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <a 
                    href={`mailto:${selectedMessage.email}`}
                    className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                  >
                    <Mail className="h-5 w-5 text-primary" />
                    <span className="text-sm truncate">{selectedMessage.email}</span>
                  </a>
                  {selectedMessage.phone && (
                    <a 
                      href={`tel:${selectedMessage.phone}`}
                      className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                    >
                      <Phone className="h-5 w-5 text-green-500" />
                      <span className="text-sm">{selectedMessage.phone}</span>
                    </a>
                  )}
                </div>

                {/* Project Type */}
                {selectedMessage.projectType && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Type de projet:</span>
                    <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                      {selectedMessage.projectType}
                    </span>
                  </div>
                )}

                {/* Message */}
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">Message</h4>
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <p className="whitespace-pre-wrap">{selectedMessage.message}</p>
                  </div>
                </div>

                {/* Date */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  Reçu le {formatDate(selectedMessage.createdAt)}
                </div>

                {/* Quick Actions */}
                <div className="flex gap-3 pt-4 border-t border-border">
                  <a
                    href={`mailto:${selectedMessage.email}?subject=Re: Votre demande sur Axeris Labs`}
                    className="flex-1 py-2 px-4 bg-primary text-primary-foreground rounded-lg text-center font-medium hover:bg-primary/90 transition-colors"
                  >
                    Répondre par email
                  </a>
                  {selectedMessage.phone && (
                    <a
                      href={`tel:${selectedMessage.phone}`}
                      className="py-2 px-4 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors"
                    >
                      Appeler
                    </a>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="h-full flex items-center justify-center p-8 text-muted-foreground">
              <div className="text-center">
                <Eye className="h-12 w-12 mx-auto mb-4 opacity-20" />
                <p>Sélectionnez un message pour voir les détails</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
